import React, {
  FunctionComponent,
  ChangeEvent,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { AppStore } from "../../../app/store";
import { EnrollmentFormProps2022 } from "./../EnrollmentFormPage";
import BackNextButtons from "./../BackNextButtons";
import { FocusableHeader, SSNInput } from "../../../components";
import {
  TextField,
  ChoiceList,
  HelpDrawerToggle,
  Alert,
} from "@cmsgov/ds-medicare-gov";
import {
  useTranslate,
  getValidationObject,
  EnrollmentPages,
} from "../../../helpers";
import MedicareCardHelpDrawer from "../../../components/MedicareCardHelpDrawer";
import PlanInfo from "./../PlanInfo";
import { PlanType, PlanCategoryType, PlanSNPType } from "../../../@types";
import { SSN } from "../../../components/SSNInput";
import { validateMbi } from "../../../api";
import { isValidMedicareNumber } from "../../../helpers/enrollmentFormHelpers";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import { AnalyticsActionType } from "../../../app/contexts/Analytics/types";

const JoiningPlanInformation: FunctionComponent<{
  planType: PlanType;
  category: string;
  snp_type: PlanSNPType;
}> = ({ planType, category, snp_type }) => {
  const t = useTranslate();

  if (snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE) {
    return (
      <>
        <FormattedHTMLMessage id="medicare_info.2021.join_snp" tagName="p" />
        <ul>
          <li>{t("medicare_info.2021.join_snp_li_medicare")}</li>
          <li>{t("medicare_info.2021.join_snp_li_chronic")}</li>
          <li>{t("medicare_info.2021.join_snp_li_long_term")}</li>
        </ul>
        <p>{t("medicare_info.2021.join_snp_requirements")}</p>
      </>
    );
  }

  if (category === PlanCategoryType.PFFS) {
    return (
      <>
        <FormattedHTMLMessage id="medicare_info.2021.join_pffs" tagName="p" />
        <ul>
          <li>{t("medicare_info.2021.join_pffs_li_doctor")}</li>
          <li>{t("medicare_info.2021.join_pffs_li_providers")}</li>
          <li>{t("medicare_info.2021.join_pffs_li_plan_contact")}</li>
          {planType === PlanType.MA && (
            <li>{t("medicare_info.2021.join_pffs_li_no_drugs")}</li>
          )}
        </ul>
      </>
    );
  }

  if (category === PlanCategoryType.COST_1876) {
    return (
      <>
        <FormattedHTMLMessage
          id={
            planType === PlanType.MA
              ? "medicare_info.2021.join_cost.no_drug_coverage"
              : "medicare_info.2021.join_cost"
          }
          tagName="p"
        />
        <ul>
          <li>{t("medicare_info.2021.join_cost_li_any_doctor")}</li>
          <li>{t("medicare_info.2021.join_cost_li_providers")}</li>
          {planType === PlanType.MA && (
            <li>{t("medicare_info.2021.join_cost_li_no_drugs")}</li>
          )}
        </ul>
        <p>{t("medicare_info.2021.join_cost_contact_plan")}</p>
      </>
    );
  }

  if (planType === PlanType.PDP) {
    return (
      <>
        <FormattedHTMLMessage id="medicare_info.2021.join_pdp" tagName="p" />
        <ul>
          <li>{t("medicare_info.2021.join_pdp_li_pffs")}</li>
          <li>{t("medicare_info.2021.join_pdp_li_msa")}</li>
          <li>{t("medicare_info.2021.join_pdp_li_cost")}</li>
        </ul>
        <p>{t("medicare_info.2021.join_pdp_other_mapd")}</p>
        <p>{t("medicare_info.2021.join_pdp_contact_plan")}</p>
      </>
    );
  }

  return null;
};

const MedicareInfo: FunctionComponent<EnrollmentFormProps2022> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  const { state, dispatch } = useContext(AppStore.AppContext);
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);
  const [medicareNumberError, setMedicareNumberError] = useState<string | null>(
    null
  );
  const [medicareNumberTouched, setMedicareNumberTouched] = useState(
    !!enrollData.medicareNumber
  );
  const [ssnError, setSsnError] = useState<string | undefined>(undefined);
  const [validForm, setValidForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const alertDiv = useRef<HTMLDivElement>(null);
  const [mbiApiValid, setMbiApiValid] = useState(true);

  const toggleHelpDrawer = (): void => {
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Enroll Form",
        event_label: "Check Card",
      },
    });
    setHelpDrawerOpen(!helpDrawerOpen);
  };

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.MedicareInfo
    );

    if (
      medicareNumberTouched &&
      (!mbiApiValid || !formStatus.medicareNumberValidity)
    ) {
      setMedicareNumberError(t("enroll_form.enter_valid_medicare"));
    } else {
      setMedicareNumberError(null);
    }

    if (submitted && !formStatus.ssnValidity) {
      setSsnError(t("enroll_form.enter_valid_ssn"));
    } else {
      setSsnError(undefined);
    }

    setValidForm(formStatus.isValid);
  };

  useEffect(() => {
    validateForm();
  }, [medicareNumberTouched, mbiApiValid]);

  useEffect(() => {
    if (submitted && !validForm) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  const next = (): void => {
    setSubmitted(true);

    if (!validForm || !mbiApiValid) {
      setMedicareNumberTouched(true);

      return;
    }

    nextPage();
  };

  useEffect(() => {
    validateForm();
  }, [enrollData]);

  useEffect(() => {
    const { medicareNumber } = enrollData;

    // If we aren't allowing HICNs, then the only valid Medicare number is MBI
    // Send the MBI to the API for secondary validation
    if (isValidMedicareNumber(medicareNumber)) {
      setMbiApiValid(false);
      validateMbi(medicareNumber).then(res => setMbiApiValid(res.valid));
    }
  }, [enrollData.medicareNumber]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.your_medicare_info")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />

        <div ref={alertDiv}>
          {!validForm && submitted && (
            <Alert
              className="ds-u-margin-top--2"
              heading={t("enroll_form.needs_your_attention")}
              variation="error"
            >
              {t("enroll_form.complete_required")}
            </Alert>
          )}
        </div>

        <section>
          <TextField
            name="medicareNumber"
            labelClassName="e2e-enroll-medicare-number ds-c-label--input"
            errorMessage={medicareNumberError}
            disabled={
              state.beneficiary && !!state.beneficiary.mbi && !state.csr
            }
            label={t("enroll_form.medicare_number")}
            value={enrollData.medicareNumber}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              const medicareNumber = e.target.value
                .trim()
                .replace(/-/g, "")
                .toUpperCase();

              setEnrollData({
                ...enrollData,
                medicareNumber,
              });

              setMedicareNumberTouched(true);
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                medicareNumber: e.target.value.trim(),
              });
            }}
            hint={
              <HelpDrawerToggle
                helpDrawerOpen={helpDrawerOpen}
                showDrawer={toggleHelpDrawer}
                className="MedicareHelpDrawer__hint"
              >
                {t("medicare_help_drawer.where_to_find")}
              </HelpDrawerToggle>
            }
          />
          {helpDrawerOpen && (
            <MedicareCardHelpDrawer onCloseClick={toggleHelpDrawer} />
          )}

          {plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE && (
            <SSNInput
              label={
                <>
                  {t("enroll_form.social_security_number")}{" "}
                  <span className="mct-u-text-transform--none">
                    {t("enroll_form.optional")}
                  </span>
                </>
              }
              value={enrollData.ssn}
              errorMessage={ssnError}
              onChange={(ssn: SSN): void => {
                setEnrollData({ ...enrollData, ssn: ssn });
              }}
            />
          )}
        </section>

        <section>
          <h2 className="ds-text-heading--xl">
            {t("medicare_info.2021.important_info")}
          </h2>
          <p>
            <FormattedHTMLMessage id="medicare_info.2021.important_info_text" />
          </p>

          <JoiningPlanInformation
            planType={plan.plan_type}
            category={plan.category}
            snp_type={plan.snp_type}
          />

          <h2 className="ds-text-heading--xl">
            {t("medicare_info.2021.privacy_act_statement")}
          </h2>
          <p>{t("medicare_info.2021.privacy_act_statement_text")}</p>

          <div>
            <ChoiceList
              type="checkbox"
              choices={[
                {
                  label: t("enroll_form.read_and_understood_page_contents"),
                  value: "understand_contents",
                  checked: enrollData.understoodCoverageLossWarning,
                },
              ]}
              errorMessage={
                submitted && !enrollData.understoodCoverageLossWarning
                  ? t("enroll_form.click_box")
                  : undefined
              }
              label=""
              name="understand"
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  understoodCoverageLossWarning: e.target.checked,
                });
              }}
              className="EnrollmentFormPage__checkbox"
            />
          </div>
        </section>

        <BackNextButtons
          nextPage={next}
          previousPage={previousPage}
          inReview={inReview}
        />
      </div>
    </div>
  );
};

export default MedicareInfo;
